import {
	Dispatch,
	FC,
	SetStateAction,
	useRef,
	useState
} from "react";
import {makeStyles} from "tss-react/mui";
import {Alert, Box, Typography} from "@mui/material";
import {useImproveAxios} from "@/app2/shared/hooks/useImproveAxios";
import {
	brightcoveInit,
	brightcoveComplete,
	type BrightcoveInitData,
	type BrightcoveApiConfig,
	type BrightcoveCompleteResponse
} from "@/app2/shared/data/repos/brightcove";

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB

/**
 * Container extensions accepted for Brightcove ingest (common Video Cloud–compatible sources).
 * Used for `accept` (after `video/*`), client validation, and UI copy — must stay in sync.
 */
export const BRIGHTCOVE_UPLOAD_VIDEO_EXTENSIONS: readonly string[] = [
	"3g2",
	"3gp",
	"avi",
	"flv",
	"m4v",
	"m2ts",
	"m2v",
	"mkv",
	"mov",
	"mp4",
	"mpeg",
	"mpg",
	"mts",
	"ogv",
	"webm",
	"wmv"
];

const BRIGHTCOVE_VIDEO_EXT_SET = new Set(BRIGHTCOVE_UPLOAD_VIDEO_EXTENSIONS);

export const BRIGHTCOVE_UPLOAD_VIDEO_FORMATS_HELPER =
	BRIGHTCOVE_UPLOAD_VIDEO_EXTENSIONS.map((e) => `.${e}`).join(", ");

/** `video/*` helps mobile pickers show the camera roll; extension list covers edge cases (e.g. uppercase .MOV). Validation stays in {@link isAllowedBrightcoveVideoFile}. */
export const BRIGHTCOVE_UPLOAD_FILE_ACCEPT = [
	"video/*",
	...BRIGHTCOVE_UPLOAD_VIDEO_EXTENSIONS.map((e) => `.${e}`)
].join(",");

function fileExtensionLower(fileName: string): string {
	const dot = fileName.lastIndexOf(".");
	if (dot < 0) return "";
	return fileName.slice(dot + 1).toLowerCase();
}

const READ_HEADER_BYTES = 4096;

async function readFileHeaderBytes(file: File, maxBytes: number): Promise<Uint8Array> {
	const slice = file.slice(0, Math.min(maxBytes, file.size)) as Blob;
	if (typeof slice.arrayBuffer === "function") {
		const buf = await slice.arrayBuffer();
		return new Uint8Array(buf);
	}
	// JSDOM: sliced Blob may not implement arrayBuffer()
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (): void => {
			const {result} = reader;
			if (result instanceof ArrayBuffer) {
				resolve(new Uint8Array(result));
			} else {
				reject(new Error("Unexpected FileReader result"));
			}
		};
		reader.onerror = (): void => {
			reject(reader.error ?? new Error("FileReader failed"));
		};
		reader.readAsArrayBuffer(slice);
	});
}

function bytesAsciiMatch(bytes: Uint8Array, offset: number, ascii: string): boolean {
	if (offset + ascii.length > bytes.length) return false;
	for (let i = 0; i < ascii.length; i++) {
		if (bytes[offset + i] !== ascii.charCodeAt(i)) return false;
	}
	return true;
}

function readUInt32BE(bytes: Uint8Array, offset: number): number {
	return (
		(bytes[offset] << 24) |
		(bytes[offset + 1] << 16) |
		(bytes[offset + 2] << 8) |
		bytes[offset + 3]
	) >>> 0;
}

/** Known non-video payloads often renamed with a video extension (empty / octet-stream MIME). */
function hasBlockedDocumentOrImageSignature(bytes: Uint8Array): boolean {
	if (bytes.length < 4) return false;
	// PDF
	if (bytesAsciiMatch(bytes, 0, "%PDF")) return true;
	// PNG
	if (
		bytes[0] === 0x89 &&
		bytes[1] === 0x50 &&
		bytes[2] === 0x4e &&
		bytes[3] === 0x47
	) {
		return true;
	}
	// JPEG
	if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return true;
	// GIF
	if (bytesAsciiMatch(bytes, 0, "GIF87a") || bytesAsciiMatch(bytes, 0, "GIF89a")) return true;
	// BMP
	if (bytesAsciiMatch(bytes, 0, "BM")) return true;
	// WebP (image or animation — not a Brightcove ingest source here)
	if (
		bytes.length >= 12 &&
		bytesAsciiMatch(bytes, 0, "RIFF") &&
		bytesAsciiMatch(bytes, 8, "WEBP")
	) {
		return true;
	}
	return false;
}

/**
 * Walk ISO BMFF / QuickTime top-level atoms (often .mov / .mp4 / .m4v).
 * Handles moov-/mdat-first files and leading `wide`/`free` padding before `ftyp`.
 */
function hasIsoBmffOrQuickTimeLeadingAtoms(bytes: Uint8Array): boolean {
	if (bytes.length < 8) return false;
	let offset = 0;
	const maxAtoms = 32;
	for (let i = 0; i < maxAtoms && offset + 8 <= bytes.length; i++) {
		const size = readUInt32BE(bytes, offset);
		if (size < 8) return false;
		if (size === 1) {
			// 64-bit atom size (rare as first atom); avoid mis-parsing.
			return false;
		}

		if (bytesAsciiMatch(bytes, offset + 4, "ftyp")) return true;
		if (bytesAsciiMatch(bytes, offset + 4, "moov")) return true;
		if (bytesAsciiMatch(bytes, offset + 4, "mdat")) return true;

		const skippable =
			bytesAsciiMatch(bytes, offset + 4, "wide") ||
			bytesAsciiMatch(bytes, offset + 4, "free") ||
			bytesAsciiMatch(bytes, offset + 4, "skip") ||
			bytesAsciiMatch(bytes, offset + 4, "pnot") ||
			bytesAsciiMatch(bytes, offset + 4, "uuid") ||
			bytesAsciiMatch(bytes, offset + 4, "junk") ||
			bytesAsciiMatch(bytes, offset + 4, "meta");
		if (skippable) {
			if (offset + size > bytes.length) {
				// Header slice ends before this atom does — cannot walk further.
				return false;
			}
			offset += size;
			continue;
		}

		return false;
	}
	return false;
}

function hasFlvSignature(bytes: Uint8Array): boolean {
	return (
		bytes.length >= 4 &&
		bytes[0] === 0x46 &&
		bytes[1] === 0x4c &&
		bytes[2] === 0x56 &&
		bytes[3] === 0x01
	);
}

function hasRecognizedVideoContainerSignature(bytes: Uint8Array): boolean {
	if (bytes.length < 4) return false;
	if (hasFlvSignature(bytes)) return true;
	if (bytes.length >= 8 && hasIsoBmffOrQuickTimeLeadingAtoms(bytes)) return true;
	if (bytes.length < 12) return false;
	// AVI (RIFF … AVI )
	if (bytesAsciiMatch(bytes, 0, "RIFF") && bytesAsciiMatch(bytes, 8, "AVI ")) return true;
	// Matroska / WebM (EBML)
	if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
		return true;
	}
	// ASF (common for WMV)
	if (bytes[0] === 0x30 && bytes[1] === 0x26 && bytes[2] === 0xb2 && bytes[3] === 0x75) {
		return true;
	}
	// MPEG program stream
	if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0x01 && bytes[3] === 0xba) {
		return true;
	}
	// MPEG transport stream sync byte
	if (bytes[0] === 0x47 && bytes.length >= 190 && bytes[188] === 0x47) return true;
	return false;
}

/**
 * Client-side gate before Brightcove init/S3 upload.
 * Rejects wrong-type files renamed with a video extension (often reported as octet-stream or "").
 */
export async function isAllowedBrightcoveVideoFile(file: File): Promise<boolean> {
	const ext = fileExtensionLower(file.name);
	if (!BRIGHTCOVE_VIDEO_EXT_SET.has(ext)) return false;

	const head = await readFileHeaderBytes(file, READ_HEADER_BYTES);
	if (hasBlockedDocumentOrImageSignature(head)) return false;

	const mime = file.type.trim().toLowerCase();
	if (mime.startsWith("image/") || mime === "application/pdf") return false;
	if (mime.startsWith("text/")) return false;

	if (
		mime.startsWith("video/") ||
		mime === "application/mp4" ||
		mime === "application/x-mpegurl" ||
		// Some browsers / OSes label QuickTime / MPEG-4 containers as audio/*.
		mime === "audio/mp4" ||
		mime === "audio/quicktime"
	) {
		return true;
	}

	// Empty, octet-stream, or non-standard types (e.g. application/quicktime, application/x-m4v):
	// same binary container check as for unknown payloads with a video extension.
	return hasRecognizedVideoContainerSignature(head);
}

export type VideoUploadType = "idle" | "progress" | "done" | "error";

/** Passed with `handleSuccess` when S3 upload finished but ingest waits for a saved entity id (new lesson or announcement). */
export type PendingBrightcoveComplete = {
	pendingBrightcoveComplete: true;
	videoUrl: string;
};

const useStyles = makeStyles()((_theme) => ({
	fileInput: {
		display: "none"
	}
}));

function isInitSuccess(
	res: { success: boolean; data?: BrightcoveInitData; error?: string }
): res is { success: true; data: BrightcoveInitData } {
	return res.success === true && res.data != null;
}

export type CompleteBrightcoveIngestParams = {
	entityType: "lesson" | "announcement";
	entityId: number;
	videoId: string;
	videoUrl: string;
};

/**
 * Calls the Brightcove complete (ingest) endpoint after the entity row exists.
 * Use when upload was deferred (e.g. new announcement): call after `announcementId` is known.
 */
export async function completeBrightcoveIngest(
	config: BrightcoveApiConfig,
	params: CompleteBrightcoveIngestParams
): Promise<BrightcoveCompleteResponse> {
	const body =
		params.entityType === "announcement"
			? {
				videoId: params.videoId,
				videoUrl: params.videoUrl,
				announcementId: params.entityId
			}
			: {
				videoId: params.videoId,
				videoUrl: params.videoUrl,
				lessonId: params.entityId
			};
	return brightcoveComplete(config, body);
}

export type UploadBrightcoveVideoProps = {
	/** "lesson" | "announcement" – complete body sends lessonId or announcementId */
	entityType?: "lesson" | "announcement";
	uploadState: VideoUploadType;
	setUploadState: Dispatch<SetStateAction<VideoUploadType>>;
	handleSuccess: (
		videoId: string,
		pending?: PendingBrightcoveComplete
	) => void;
	children: (props: {
		onClick: () => void;
		progress: number;
		/** Render this where the parent layout needs the upload error to appear. */
		error: JSX.Element | null;
	}) => JSX.Element;
	errorPlacement?: "default" | "children";
	/**
	 * Lesson ID or announcement ID. When there is no id yet, pass 0: upload runs init + S3 only;
	 * parent must call {@link completeBrightcoveIngest} after the entity is saved.
	 */
	lessonId: number;
};

/**
 * Brightcove upload for app2: uses merged API /api/_internal/video/brightcove (init + complete).
 * Supports lessons and announcements via entityType + lessonId (used as entityId).
 */
export const UploadBrightcoveVideo: FC<UploadBrightcoveVideoProps> = ({
	uploadState,
	setUploadState,
	handleSuccess,
	children,
	lessonId,
	errorPlacement = "default",
	entityType = "lesson"
}) => {
	const {classes} = useStyles();
	const apiConfig = useImproveAxios() as BrightcoveApiConfig;
	const inputRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [progress, setProgress] = useState(0);

	const handleClick = (): void => {
		inputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const files = e.target.files;
		const selectedFile = files?.[0];

		const resetInput = (): void => {
			if (inputRef.current) {
				inputRef.current.value = "";
			}
		};

		if (!selectedFile) {
			resetInput();
			return;
		}

		if (selectedFile.size > MAX_FILE_SIZE) {
			setError("File size exceeds the maximum limit of 10 GB");
			setUploadState("error");
			resetInput();
			return;
		}

		void (async (): Promise<void> => {
			setProgress(0);
			const allowed = await isAllowedBrightcoveVideoFile(selectedFile);
			if (!allowed) {
				setError(
					`Unsupported file type. Use a supported video file: ${BRIGHTCOVE_UPLOAD_VIDEO_FORMATS_HELPER}.`
				);
				setUploadState("error");
				resetInput();
				return;
			}

			setError(null);
			setUploadState("progress");
			try {
				await uploadFile(selectedFile, handleSuccess);
			} catch {
				setError("Failed to upload video");
				setUploadState("error");
			} finally {
				resetInput();
			}
		})();
	};

	const uploadFile = async (
		file: File,
		successCb: UploadBrightcoveVideoProps["handleSuccess"]
	): Promise<void> => {
		setProgress(0);

		const deferIngest = lessonId < 1;

		const initResp = await brightcoveInit(apiConfig, {fileName: file.name});
		if (!isInitSuccess(initResp)) {
			setError(initResp.error ?? "Failed to initialize upload");
			setUploadState("error");
			return;
		}

		const {S3Client} = await import("@aws-sdk/client-s3");
		const {Upload} = await import("@aws-sdk/lib-storage");

		const s3 = new S3Client({
			region: "us-east-1",
			credentials: {
				accessKeyId: initResp.data.credentials.access_key_id,
				secretAccessKey: initResp.data.credentials.secret_access_key,
				sessionToken: initResp.data.credentials.session_token
			}
		});

		const uploader = new Upload({
			client: s3,
			params: {
				Bucket: initResp.data.credentials.bucket,
				Key: initResp.data.credentials.object_key,
				Body: file
			},
			//10 MB chunks
			partSize: 10 * 1024 * 1024
		});

		uploader.on("httpUploadProgress", (evt) => {
			if (evt.loaded != null && evt.total != null) {
				setProgress((evt.loaded / evt.total) * 100);
			}
		});

		await uploader.done();

		const videoUrl = initResp.data.credentials.api_request_url;
		const vid = initResp.data.videoId;

		if (deferIngest) {
			successCb(vid, {
				pendingBrightcoveComplete: true,
				videoUrl
			});
			setUploadState("done");
			return;
		}

		const completeBody = {
			videoId: vid,
			videoUrl,
			...(entityType === "announcement"
				? {announcementId: lessonId}
				: {lessonId})
		};
		const completeResp = await brightcoveComplete(apiConfig, completeBody);

		if (!completeResp.success) {
			setError("error" in completeResp ? completeResp.error : "Failed to ingest video");
			setUploadState("error");
			return;
		}

		successCb(vid);
		setUploadState("done");
	};

	const errorAlert = error ? (
		<Box
			sx={{
				flexBasis: "100%",
				width: "100%",
				minWidth: 0,
				mt: {xs: 1, sm: 1.5}
			}}
		>
			<Alert
				severity="error"
				variant="outlined"
				sx={(theme) => {
					const isDark = theme.palette.mode === "dark";
					const paper = theme.palette.background.paper;
					if (isDark) {
						const msg = theme.palette.error.contrastText;
						return {
							width: "100%",
							maxWidth: "100%",
							boxSizing: "border-box",
							minWidth: 0,
							py: 0.75,
							alignItems: "flex-start",
							borderWidth: 1,
							borderColor: `color-mix(in srgb, ${theme.palette.error.light} 55%, transparent)`,
							backgroundColor: `color-mix(in srgb, ${theme.palette.error.main} 22%, transparent)`,
							color: msg,
							"& .MuiAlert-icon": {color: msg},
							"& .MuiAlert-message": {
								flex: "1 1 0%",
								minWidth: 0,
								maxWidth: "100%",
								width: 0,
								color: msg,
								whiteSpace: "normal",
								wordBreak: "break-word",
								overflowWrap: "anywhere"
							}
						};
					}
					return {
						width: "100%",
						maxWidth: "100%",
						boxSizing: "border-box",
						minWidth: 0,
						py: 0.75,
						alignItems: "flex-start",
						borderWidth: 1,
						borderColor: `color-mix(in srgb, ${theme.palette.error.main} 45%, transparent)`,
						backgroundColor: `color-mix(in srgb, ${theme.palette.error.main} 8%, ${paper})`,
						color: theme.palette.error.dark,
						"& .MuiAlert-icon": {color: theme.palette.error.main},
						"& .MuiAlert-message": {
							flex: "1 1 0%",
							minWidth: 0,
							maxWidth: "100%",
							width: 0,
							color: theme.palette.error.dark,
							whiteSpace: "normal",
							wordBreak: "break-word",
							overflowWrap: "anywhere"
						}
					};
				}}
			>
				<Typography
					component="div"
					variant="body2"
					sx={{
						whiteSpace: "normal",
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						maxWidth: "100%"
					}}
				>
					{error}
				</Typography>
			</Alert>
		</Box>
	) : null;

	return (
		<Box sx={{display: "contents"}}>
			<input
				ref={inputRef}
				type="file"
				onChange={handleFileChange}
				accept={BRIGHTCOVE_UPLOAD_FILE_ACCEPT}
				className={classes.fileInput}
			/>
			{children({
				onClick: handleClick,
				progress,
				error: errorPlacement === "children" ? errorAlert : null
			})}
			{errorPlacement === "default" ? errorAlert : null}
		</Box>
	);
};
